import { useMemo, useRef } from 'react';
import { arc as d3arc, pie as d3pie } from 'd3-shape';
import { useThemeColors, buildCategoricalPalette } from './useThemeColors.js';

/**
 * Donut de répartition — SVG maison (d3-shape arc/pie).
 *
 * data : [{ label, value, color? }]
 */
export default function Donut({ data = [], size = 220, thickness = 36, centerLabel }) {
  const colors = useThemeColors();
  const svgRef = useRef(null);
  const palette = buildCategoricalPalette(colors);

  const radius = size / 2;
  const arcs = useMemo(() => {
    const total = data.reduce((a, d) => a + (d.value || 0), 0);
    if (total <= 0) return [];
    const pieGen = d3pie()
      .sort(null)
      .value((d) => d.value || 0);
    const arcGen = d3arc()
      .innerRadius(radius - thickness)
      .outerRadius(radius);
    return pieGen(data).map((p, i) => ({
      d: arcGen(p),
      color: data[i].color || palette[i % palette.length],
      label: data[i].label,
      value: data[i].value || 0,
      pct: Math.round(((data[i].value || 0) / total) * 100),
    }));
  }, [data, radius, thickness, palette]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <svg
        ref={svgRef}
        width={size}
        height={size}
        role="img"
        data-testid="donut-chart"
        style={{ display: 'block' }}
      >
        <g transform={`translate(${radius},${radius})`}>
          {arcs.length === 0 ? (
            <circle r={radius - thickness / 2} fill="none" stroke={colors.glassBorder} strokeWidth={thickness} />
          ) : (
            arcs.map((a) => (
              <path key={a.label} data-testid={`donut-slice-${a.label}`} d={a.d} fill={a.color}>
                <title>{`${a.label} : ${a.value}h (${a.pct}%)`}</title>
              </path>
            ))
          )}
          {centerLabel != null && (
            <text textAnchor="middle" dy="0.32em" fontSize={20} fontWeight={600} fill={colors.text}>
              {centerLabel}
            </text>
          )}
        </g>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {arcs.map((a) => (
          <div key={a.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: a.color,
                display: 'inline-block',
              }}
            />
            <span style={{ color: 'var(--text)' }}>{a.label}</span>
            <span style={{ color: 'var(--text-muted)' }}>
              {a.value}h ({a.pct}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
